import React, { Fragment, useEffect, useState } from "react";
import { useHistory, useParams } from 'react-router-dom';
import ApiRest from "../../../service/ApiRest";
import { toast } from "react-toastify";
import CandidateForm from "../../Forms/CandidateForm";
import type { Candidate, PhoneNumber, Address } from "../../Forms/CandidateForm";

const moduleName = 'candidate';

interface ParamTypes {
    id: string;
}

const defElement: Candidate = {
    id: -1,
    firstName: '',
    lastName: '',
    fullName: '',
    birthDate: '',
    citizenship: '',
    position: '',
    applicationDate: new Date().toISOString().split('T')[0],
    reviewDate: '',
    status: 'review',
    email: '',
    personalId: '',
    phoneNumbers: [],
    addresses: [],
    comment: '',
    history: []
};

export default function CandidatesPage() {
    const history = useHistory();
    const { id } = useParams<ParamTypes>();
    const [element, setElement] = useState<Candidate>(defElement);
    const [isLoading, setIsLoading] = useState(false);

    const saveData = async (data: Partial<Candidate>) => {
        try {
            setIsLoading(true);
            
            const formattedData = {
                ...data,
                fullName: `${data.firstName} ${data.lastName}`.trim(),
                phoneNumbers: data.phoneNumbers?.map((phone: PhoneNumber) => ({
                    ...phone,
                    comment: phone.comment || '',
                    id: typeof phone.id === 'number' && phone.id < 0 ? undefined : phone.id
                })) || [],
                addresses: data.addresses?.map((addr: Address) => ({
                    ...addr,
                    comment: addr.comment || '',
                    id: typeof addr.id === 'number' && addr.id < 0 ? undefined : addr.id
                })) || []
            };

            if (id === 'new') {
                const res = await ApiRest.addElement(moduleName, formattedData);
                toast.success('Įrašas išsaugotas');
                setElement(res);
                history.replace('/' + moduleName + '/' + res.id);
            } else {
                const { history: newHistory, ...otherData } = formattedData;
                const res = await ApiRest.updateElement(
                    moduleName,
                    element.id,
                    {
                        ...otherData,
                        history: newHistory ? newHistory.map(h => ({
                            id: typeof h.id === 'number' && h.id < 0 ? undefined : h.id,
                            date: h.date,
                            change: h.change
                        })) : []
                    }
                );
                toast.success('Įrašas išsaugotas');
                setElement(res);
            }
        } catch (error) {
            console.error('Error saving/updating candidate:', error);
            toast.error('Klaida išsaugant įrašą');
        } finally {
            setIsLoading(false);
        }
    };

    const getData = async () => {
        if (id === 'new') {
            setElement({
                ...defElement,
                id: -1,
                applicationDate: new Date().toISOString().split('T')[0]
            });
            return;
        }

        try {
            setIsLoading(true);
            const res = await ApiRest.getElement(moduleName, parseInt(id, 10));
            
            const formattedElement = {
                ...res,
                phoneNumbers: (res.phoneNumbers || []).map((phone: PhoneNumber) => ({
                    ...phone,
                    comment: phone.comment || ''
                })),
                addresses: (res.addresses || []).map((addr: Address) => ({
                    ...addr,
                    comment: addr.comment || ''
                })),
                history: res.history || []
            };
            
            setElement(formattedElement);
        } catch (error) {
            console.error('Error loading candidate:', error);
            toast.error('Klaida įkeliant kandidato duomenis');
            history.push('/' + moduleName);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, [id]);

    const form = (!isLoading && (element.id === -1 || element.id > 0)) ? (
        <Fragment>
            <CandidateForm 
                element={element} 
                saveData={saveData}
            />
        </Fragment>
    ) : null;

    return (
        <Fragment>
            <div style={{ maxWidth: '100%', margin: '0 auto', padding: '0 20px' }}>
                {form}
            </div>
        </Fragment>
    );
}